import React from 'react';
import { Star, ExternalLink, Calendar, BookOpen } from 'lucide-react';
import { siteData } from '../data/mock';

const BooksPage = () => {
  const { books } = siteData;

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="heading-1 mb-6">{books.title}</h1>
          <p className="body-large text-lime-400 mb-4">{books.subtitle}</p>
          <p className="body-medium text-gray-300 max-w-2xl mx-auto">
            {books.description}
          </p>
        </div>
      </section>

      {/* Published Books Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-12 text-center">Published Works</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {books.published.map((book) => (
              <div key={book.id} className="flex flex-col lg:flex-row gap-8">
                {/* Book Cover */}
                <div className="lg:w-1/3 flex-shrink-0">
                  <img 
                    src={book.cover} 
                    alt={book.title}
                    className={
                      book.coverContain
                        ? "w-full max-w-sm mx-auto lg:max-w-none object-contain drop-shadow-2xl"
                        : "w-full max-w-sm mx-auto lg:max-w-none rounded-lg shadow-2xl"
                    }
                  />
                </div>
                
                {/* Book Details */}
                <div className="lg:w-2/3">
                  <div className="mb-4">
                    {book.status && (
                      <span className="caption text-lime-400 bg-lime-400/10 px-3 py-1 rounded-full">
                        {book.status}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="heading-2 mb-2">{book.title}</h3>
                  {book.subtitle && (
                    <p className="body-medium text-lime-400 mb-4 tracking-wide">{book.subtitle}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 mb-6 text-gray-400">
                    <div className="flex items-center">
                      <BookOpen size={16} className="mr-2" />
                      <span className="body-small">{book.genre}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-2" />
                      <span className="body-small">{book.publishDate}</span>
                    </div>
                  </div>
                  
                  <p className="body-medium mb-6 text-gray-300">
                    {book.description}
                  </p>
                  
                  {/* Reviews */}
                  {book.reviews && (
                    <div className="mb-6">
                      <div className="flex items-center mb-4">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={18} className="text-lime-400 fill-current" />
                          ))}
                        </div>
                        <span className="ml-2 text-gray-400 body-small">
                          5.0 ({book.reviews.length} reviews)
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {book.reviews.slice(0, 2).map((review, index) => (
                          <div key={index} className="bg-gray-800/50 p-4 rounded-lg">
                            <div className="flex items-center mb-2">
                              <div className="flex">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} size={14} className="text-lime-400 fill-current" />
                                ))}
                              </div>
                              <span className="ml-2 font-semibold text-white text-sm">
                                {review.name}
                              </span>
                            </div>
                            <p className="body-small text-gray-300 italic">
                              "{review.comment}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Purchase Section */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {book.purchaseLink !== '#' ? (
                      <a 
                        href={book.purchaseLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-primary"
                      >
                        Buy Now{book.price && book.price.startsWith('$') ? ` - ${book.price}` : ''}
                        <ExternalLink size={16} className="ml-2" />
                      </a>
                    ) : (
                      <button className="btn-primary opacity-50 cursor-not-allowed">
                        {book.status || 'Coming Soon'}
                      </button>
                    )}
                    <button className="btn-secondary">
                      Preview Sample
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Features Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">Why Readers Love These Books</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Each book is crafted with purpose, drawing from real-life experiences and biblical wisdom to provide practical guidance for your spiritual journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="service-card text-center">
              <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen size={32} className="text-black" />
              </div>
              <h3 className="heading-3 mb-3">Biblical Foundation</h3>
              <p className="body-small text-gray-300">
                Every principle and teaching is rooted in Scripture, providing solid theological grounding for practical application.
              </p>
            </div>

            <div className="service-card text-center">
              <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star size={32} className="text-black" />
              </div>
              <h3 className="heading-3 mb-3">Real-Life Application</h3>
              <p className="body-small text-gray-300">
                Practical insights and actionable steps help you apply spiritual principles to everyday challenges and situations.
              </p>
            </div>

            <div className="service-card text-center">
              <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={32} className="text-black" />
              </div>
              <h3 className="heading-3 mb-3">Personal Journey</h3>
              <p className="body-small text-gray-300">
                Drawing from personal experiences and pastoral wisdom to provide authentic, relatable guidance for your spiritual growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center bg-gray-800/50 p-8 rounded-lg">
            <h2 className="heading-2 mb-6">Stay Updated on New Releases</h2>
            <p className="body-medium text-gray-300 mb-8">
              Be the first to know about new books, exclusive content, and special offers from Romero Sumpter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-lime-400"
              />
              <button className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BooksPage;